export interface WebStorageArea {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  getJson<T>(
    key: string,
    parse: (value: unknown) => T | null,
  ): T | null;
  setJson(key: string, value: unknown): void;
}

function resolveStorage(getStorage: () => Storage): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return getStorage();
  } catch {
    return null;
  }
}

function createWebStorageArea(getStorage: () => Storage): WebStorageArea {
  return {
    getItem(key) {
      const storage = resolveStorage(getStorage);
      if (!storage) {
        return null;
      }
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      const storage = resolveStorage(getStorage);
      if (!storage) {
        return;
      }
      try {
        storage.setItem(key, value);
      } catch {
        /* storage blocked or quota exceeded */
      }
    },
    removeItem(key) {
      const storage = resolveStorage(getStorage);
      if (!storage) {
        return;
      }
      try {
        storage.removeItem(key);
      } catch {
        /* storage blocked */
      }
    },
    getJson(key, parse) {
      const raw = this.getItem(key);
      if (!raw) {
        return null;
      }
      try {
        return parse(JSON.parse(raw));
      } catch {
        return null;
      }
    },
    setJson(key, value) {
      this.setItem(key, JSON.stringify(value));
    },
  };
}

/** Durable browser storage (localStorage). Fails closed when unavailable. */
export const localStore = createWebStorageArea(() => localStorage);

/** Tab-scoped browser storage (sessionStorage). Fails closed when unavailable. */
export const sessionStore = createWebStorageArea(() => sessionStorage);
